import { useEffect, useMemo, useState } from 'react';
import { Alert, Button, Form, Offcanvas } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { downloadPdf } from 'utils/helpers/pdf/pdfMake';
import type { TariffKey } from '../TariffItem';
import InvoicePreviewCard from './InvoicePreviewCard';
import { buildInvoicePdfDefinition } from './invoicePdfDefinition';

interface Props {
    show: boolean;
    onHide: () => void;
    accountId: number | null;
    planKey: TariffKey | null;
    amount: string;
}

interface GeneratedInvoice {
    number: string;
    timestamp: string;
    payerDetails: string;
}

const InvoiceDetailsOffcanvas = ({
    show,
    onHide,
    accountId,
    planKey,
    amount,
}: Props) => {
    const { t } = useTranslation('Tariff');
    const [payerDetails, setPayerDetails] = useState('');
    const [generatedInvoice, setGeneratedInvoice] = useState<GeneratedInvoice | null>(null);

    const selectedPlanName = useMemo(() => {
        if (!planKey) return '—';
        return t(`plans.${planKey}.name`);
    }, [planKey, t]);
    const receiverDetails = useMemo(() => ({
        name: t('receiverNameValue'),
        iban: t('receiverIbanValue'),
        edrpou: t('receiverEdrpouValue'),
        bankName: t('receiverBankValue'),
        mfo: t('receiverMfoValue'),
    }), [t]);

    useEffect(() => {
        if (!show) {
            setPayerDetails('');
            setGeneratedInvoice(null);
        }
    }, [show]);

    const handleGenerate = () => {
        const timestamp = String(Date.now());
        const invoiceNumber = `EC-${String(accountId ?? 0)}-${timestamp}`;
        setGeneratedInvoice({
            number: invoiceNumber,
            timestamp,
            payerDetails: payerDetails.trim(),
        });
    };

    const handleDownloadPdf = () => {
        if (!generatedInvoice) return;
        const docDefinition = buildInvoicePdfDefinition({
            t,
            generatedInvoice,
            receiverDetails,
            selectedPlanName,
            amount,
        });
        downloadPdf(docDefinition, `${generatedInvoice.number}.pdf`);
    };

    return (
        <Offcanvas show={show} onHide={onHide} placement="end">
            <Offcanvas.Header closeButton className="border-bottom">
                <Offcanvas.Title>{t('invoiceOffcanvasTitle')}</Offcanvas.Title>
            </Offcanvas.Header>
            <Offcanvas.Body>
                <Form>
                    <Form.Group className="mb-3" controlId="invoice-payer-details">
                        <Form.Label className="text-muted">{t('payerDetailsLabel')}</Form.Label>
                        <Form.Control
                            as="textarea"
                            rows={4}
                            placeholder={t('payerDetailsPlaceholder')}
                            value={payerDetails}
                            onChange={(e) => setPayerDetails(e.target.value)}
                        />
                    </Form.Group>

                    <Button
                        type="button"
                        className="w-100 mb-3"
                        disabled={!payerDetails.trim() || !planKey}
                        onClick={handleGenerate}
                    >
                        {t('generateInvoice')}
                    </Button>
                </Form>

                {generatedInvoice && (
                    <InvoicePreviewCard
                        generatedInvoice={generatedInvoice}
                        selectedPlanName={selectedPlanName}
                        amount={amount}
                        receiverDetails={receiverDetails}
                        onDownloadPdf={handleDownloadPdf}
                    />
                )}

                {!generatedInvoice && (
                    <Alert variant="info" className="mt-2">
                        {t('invoicePreviewHint')}
                    </Alert>
                )}
            </Offcanvas.Body>
        </Offcanvas>
    );
};

export default InvoiceDetailsOffcanvas;
